import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { GeraetStatusTyp, WaageProtokoll, WaageKonfiguration } from "../model/model";
import { Wiegedaten } from "../model/swagger-model";
import { BluetoothSerial } from "@awesome-cordova-plugins/bluetooth-serial/ngx";
import { MyApp } from "../helper/app";
import { AppStorage } from "../helper/app-storage";
import { AppConfig } from "../helper/app.config";
import { Utils } from "../helper/utils";
import { Logger } from "../helper/app-error-logger";

const STANDARD_UPDATE_INTERVAL_MS = 60_000;
const FAST_UPDATE_INTERVAL_MS = 10_000;

export abstract class WaageBase {
    log: Logger;

    konfiguration: WaageKonfiguration;
    adresse: string;
    status = new BehaviorSubject<GeraetStatusTyp>(GeraetStatusTyp.NichtVerbunden);
    dataReceiveSubscription: Subscription;
    connectSubscription: Subscription;
    aktiv: boolean;

    /**
     * Letzter Zeitpunkt an dem Daten von der Waage empfangen wurden.
     */
    lastDataReceived = 0;

    dagtumLetzteUhrSynchronisierung = 0;

    error = new Subject<string>();
    datenEmpfangen = new Subject<void>();
    wiegedatenEmpfangen = new Subject<Wiegedaten>();
    protokollChanged = new Subject<void>();
    letzteAlibinummerEmpfangen = new Subject<number>();
    alibieintragEmpfangen = new Subject<Wiegedaten>();

    /**
     * Subscriptions die beim Dispose unsubscribed werden müssen.
     */
    subscriptions: Subscription[] = [];

    updateIntervalId: any = null;

    private protokoll: WaageProtokoll;

    currentUpdateIntervalMs = -1;

    lastTouchEvent = 0;

    isConnected = false;

    connectRunning = false;
    connectDate = 0;

    onPointerDownFn: (e: TouchEvent) => void;

    verbindungsmodus: string;

    constructor(log: Logger, protected bluetoothSerial: BluetoothSerial) {
        this.log = log;
        this.log?.info('Waage constructor');

        this.onPointerDownFn = (e: TouchEvent) => this.onPointerDown();

        // Standardmäßig wird alle 60 Sekunden die Verbindung geprüft.
        this.setUpdateInterval(STANDARD_UPDATE_INTERVAL_MS);

        document.addEventListener('pointerdown', this.onPointerDownFn, true);

        this.subscriptions.push(MyApp.current.configRefreshed.subscribe(() => this.onConfigRefreshed()));
    }

    onConfigRefreshed(): void {
        this.log?.debug('onConfigRefreshed');

        this.verbindungsmodus = AppConfig.current.einstellungen?.WaageVerbindungsModus;

        if (this.verbindungsmodus === 'nur-bei-bedarf') {
            // Verbindung nur bei Bedarf herstellen.
            this.setUpdateInterval(-1);
        }
    }

    onPointerDown(): void {
        this.lastTouchEvent = Date.now();

        // Bei Benutzerinteraktion soll das Reconnect-Intervall auf 10 Sekunden reduziert werden.
        // Nach 5 Minuten wird es dann wieder hochgesetzt.
        this.setUpdateInterval(FAST_UPDATE_INTERVAL_MS);
    }

    /**
     * Setzt wie häufig die Waagen-Anbindung geprüft werden soll.
     */
    setUpdateInterval(intervalMs: number) {
        const verbindungsmodus = AppConfig.current.einstellungen?.WaageVerbindungsModus;

        if (verbindungsmodus === 'nur-bei-bedarf') {
            // Keine automatische Verbindung. Deshalb das Interval deaktivieren.
            intervalMs = -1;
        }

        if (this.currentUpdateIntervalMs === intervalMs) {
            // Keine Änderung
            return;
        }

        this.log?.info(`Setze Update-Intervall auf ${intervalMs} ms`);

        this.currentUpdateIntervalMs = intervalMs;

        if (this.updateIntervalId) {
            clearInterval(this.updateIntervalId);
            this.updateIntervalId = null;
        }

        if (intervalMs > 0) {
            this.updateIntervalId = setInterval(() => this.update(), intervalMs);
        }
    }

    /**
     * Diese Methode wird alle 30 Sekunden (oder häufiger) aufgerufen, um die Verbindung zur Waage zu prüfen.
     */
    update() {
        const verbindungsmodus = AppConfig.current.einstellungen?.WaageVerbindungsModus;

        if (verbindungsmodus === 'nur-bei-bedarf') {
            return;
        }

        if (this.connectRunning && this.connectDate > Date.now() - 30_000) {
            // Verbindung nicht prüfen. Die Verbindung zur Waage wird gerade hergestellt und darf nicht getrennt werden.
            return;
        }

        if (this.aktiv) {
            this.pruefeVerbindung();
        }

        if (this.lastTouchEvent < Date.now() - 5 * 60 * 1000) {
            // Seit 5 Minuten keine Benutzerinteraktion mehr.
            this.setUpdateInterval(STANDARD_UPDATE_INTERVAL_MS);
        }
    }

    async getProtokoll(): Promise<WaageProtokoll> {
        if (!this.protokoll) {
            this.protokoll = await AppStorage.current.get('waage-protokoll-' + this.adresse);

            if (!this.protokoll) {
                this.protokoll = {
                    waageAdresse: this.adresse,
                    eintraege: []
                }
            }
        }

        this.fixProtokoll();

        return this.protokoll;
    }

    fixProtokoll() {
        if (!this.protokoll) {
            // Das Protokoll muss zuvor geladen sein.
            return;
        }

        if (!this.protokoll.waageAdresse) {
            this.protokoll.waageAdresse = this.adresse;
        }

        if (!this.protokoll.eintraege) {
            this.protokoll.eintraege = [];
        }
    }

    abstract getTyp(): string;

    setActive(active: boolean) {
        if (this.aktiv === active) {
            // Keine Änderung
            return;
        }

        this.aktiv = active;

        if (active) {
            this.verbindungsmodus = AppConfig.current.einstellungen?.WaageVerbindungsModus;

            if (this.verbindungsmodus !== 'nur-bei-bedarf') {
                this.connect('setActive');

                this.setUpdateInterval(FAST_UPDATE_INTERVAL_MS);
            } else {
                this.setUpdateInterval(-1);
            }
        } else {
            this.close();

            this.setUpdateInterval(-1);
        }
    }

    async trenneVerbindungBeiBedarf() {
        const verbindungsmodus = AppConfig.current.einstellungen?.WaageVerbindungsModus;

        if (verbindungsmodus === 'nur-bei-bedarf') {
            await this.close();
        }
    }

    /**
     * Gibt das Trennzeichen zurück auf das beim Empfangen von Daten gewartet werden soll.
     */
    abstract getDataDelimiter(): string;

    async connect(info?: string, throwError = true): Promise<any> {
        this.log?.info('Connect: ' + info);

        if (this.connectRunning) {
            this.log.warn('Connect läuft bereits. Ignoriere weiteren Connect-Aufruf.');
            return;
        }

        if (!MyApp.isCordovaAvailable()) {
            // Simulation
            await Utils.delay(500);
            this.status.next(GeraetStatusTyp.Verbunden);
            return;
        }

        try {
            this.connectRunning = true;
            this.isConnected = false;
            this.connectDate = Date.now();

            await MyApp.ready();

            // Achtung: Diese Methode wird eine Exception, falls Bluetooth nicht aktiviert ist.
            // Wir müssen das hier aber prüfen bevor wir einen Connect-Versuch starten, da es sonst zu einem NullReferenceException in Java kommt und die App abstürzt.
            await this.bluetoothSerial.isEnabled();

            // if (MyApp.istImVordergrund) {
            //     log?.debug('Aktiviere Bluetooth...');
            //     const enableResult = await this.bluetoothSerial.enable();

            //     log?.debug('enableResult: ' + enableResult, enableResult);
            // }

            const delimiter = this.getDataDelimiter();

            this.log?.debug('Subscribe auf Data. Verwende als Delimiter: ' + delimiter);
            this.dataReceiveSubscription = this.bluetoothSerial.subscribe(delimiter).subscribe(p => this.onDataReceived(p));

            this.log?.debug('Connect: ' + this.adresse);

            let errorConnecting = false;

            // Achtung: scheiss interface: Subscribe to connect, unsubscribe to disconnect.
            this.connectSubscription = this.bluetoothSerial.connect(this.adresse).subscribe({
                next: (value) => {
                    this.log?.debug('Connect Next: ' + value);
                    this.isConnected = true;
                },
                error: (err) => {
                    const errMessage = Utils.getErrorMessage(err);

                    this.log.warn('Connect Error: ' + errMessage);
                    this.status.next(GeraetStatusTyp.NichtVerbunden);
                    this.error.next('Not connected: ' + errMessage);

                    this.isConnected = false;

                    errorConnecting = true;
                },
                complete: () => {
                    this.log?.debug('Connect Complete');
                }
            });

            // Warte max. 20 Sekunden bis die Waage verbunden ist
            const timeout = Date.now() + 20_000;

            let verbunden = false;
            let counter = 0;

            while (Date.now() < timeout) {
                counter++;

                await Utils.delay(1000);

                try {
                    if (this.isConnected) {
                        verbunden = true;
                        break;
                    }

                    if (errorConnecting) {
                        verbunden = false;
                        break;
                    }

                    await this.bluetoothSerial.isConnected();
                    this.log?.debug('isConnected 2: TRUE');
                    verbunden = true;
                    break;
                } catch (err) {
                    // Nicht verbunden
                    this.log?.debug('isConnected 2 (Counter: ' + counter + '): ' + Utils.getErrorMessage(err));
                }
            }

            if (!verbunden) {
                throw new Error('Not connected (timeout)');
            }

            this.status.next(GeraetStatusTyp.Verbunden);
        } catch (err) {
            this.log.warn(`Fehler im Connect Waage (${this.adresse}): ` + Utils.getErrorMessage(err), err);
            this.status.next(GeraetStatusTyp.NichtVerbunden);

            let errMessage = Utils.getErrorMessage(err);

            if (errMessage.indexOf('User did not enable Bluetooth') >= 0) {
                errMessage = 'Bluetooth nicht aktiv';
            }

            this.error.next(errMessage);

            if (throwError) {
                throw err;
            }
        } finally {
            this.connectRunning = false;
        }
    }

    async close(): Promise<any> {
        this.log?.debug('Close');

        this.status.next(GeraetStatusTyp.NichtVerbunden);

        if (!MyApp.isCordovaAvailable()) {
            return;
        }

        try {
            if (this.connectSubscription) {
                this.connectSubscription.unsubscribe();
                this.connectSubscription = null;
            }
        } catch (err) {
            this.log.warn(`Close Waage (connectSubscription): ` + Utils.getErrorMessage(err));
        }

        try {
            if (this.dataReceiveSubscription) {
                this.dataReceiveSubscription.unsubscribe();
                this.dataReceiveSubscription = null;
            }
        } catch (err) {
            this.log.warn(`Close Waage (dataReceiveSubscription): ` + Utils.getErrorMessage(err));
        }

        try {
            this.log?.debug('Disconnecting bluetooth...');
            await this.bluetoothSerial.disconnect();
            this.log?.debug('Disconnected');
        } catch (err) {
            this.error.next(Utils.getErrorMessage(err));
            this.log.warn(`Close Waage: ${err.message}`);
        }

        this.isConnected = false;
    }

    async dispose(): Promise<void> {
        this.log?.debug('dispose');

        this.aktiv = false;

        document.removeEventListener('pointerdown', this.onPointerDownFn, true);

        if (this.updateIntervalId) {
            clearInterval(this.updateIntervalId);
            this.updateIntervalId = null;
        }

        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];

        await this.close();

        Utils.removeAllSubscribers(
            this.error,
            this.datenEmpfangen,
            this.wiegedatenEmpfangen,
            this.protokollChanged,
            this.letzteAlibinummerEmpfangen,
            this.alibieintragEmpfangen
        );
    }

    async pruefeVerbindung(info?: string): Promise<void> {
        this.log?.debug('pruefeVerbindung: ' + info);

        if (!this.aktiv) {
            this.status.next(GeraetStatusTyp.NichtVerbunden);
            return;
        }

        if (!MyApp.isCordovaAvailable()) {
            return;
        }

        if (this.connectRunning && this.connectDate > Date.now() - 30_000) {
            // Warten bis die Verbindung hergestellt wurde.
            const timeout = Date.now() + 10_000;

            while (this.connectRunning && Date.now() < timeout) {
                this.log?.debug('pruefeVerbindung: Warte bis connectRunning false ist. connectDate=' + this.connectDate);

                await Utils.delay(1000);
            }
        }

        let connected = false;

        try {
            await this.bluetoothSerial.isConnected();

            connected = true;

            this.log?.debug('isConnected: TRUE');
            this.status.next(GeraetStatusTyp.Verbunden);

        } catch (err) {
            // Nicht verbunden
            this.log?.debug('isConnected: ' + Utils.getErrorMessage(err), err);
            this.status.next(GeraetStatusTyp.NichtVerbunden);
        }

        try {
            if (!connected) {
                this.log?.debug('Waage nicht verbunden. Versuche reconnect...');

                await this.close();
                await this.connect('pruefeVerbindung');
            }
        } catch (err) {
            this.log.warn('FEHLER pruefeVerbindung: ' + Utils.getErrorMessage(err), err);
            this.error.next(Utils.getErrorMessage(err));
            this.status.next(GeraetStatusTyp.Fehler);
        }
    }

    speichereProtokoll() {
        // Zu alte Einträge löschen
        const maxEintraege = 100;
        const protokoll = this.protokoll;

        if (protokoll.eintraege.length > maxEintraege) {
            protokoll.eintraege.splice(0, protokoll.eintraege.length - maxEintraege);
        }

        AppStorage.current.set('waage-protokoll-' + this.adresse, protokoll);

        this.protokollChanged.next();
    }

    abstract onDataReceived(message: string): Promise<void>;
}
